import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Activity, Database, FileCheck, Mail, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOB_LABELS = {
  auto_close_sweep: 'Auto clock-out & break reminders (every 5 min)',
  qb_push_reminder: 'QuickBooks push reminders (hourly)',
  exception_sweep: 'Stale exception sweep (every 6 h)',
  exception_digest: 'Morning exception digest (hourly check)',
};

const ago = (s) => {
  if (s == null) return 'never';
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

const Dot = ({ ok }) => (
  ok
    ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
    : <XCircle className="w-4 h-4 text-red-400 inline" />
);

const Panel = ({ title, icon: Icon, ok, children, testId }) => (
  <div className="rounded-xl p-4" style={{ background: '#121D2E', border: `1px solid ${ok === false ? '#7F1D1D' : '#2D3748'}` }} data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-blue-300" />
      <h2 className="text-sm font-semibold text-white">{title}</h2>
      {ok !== undefined && <span className="ml-auto">{ok ? <Dot ok /> : <Dot ok={false} />}</span>}
    </div>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between text-xs py-1 border-b border-[#1F2D3F] last:border-0">
    <span style={{ color: '#94A3B8' }}>{label}</span>
    <span className="text-white text-right">{value}</span>
  </div>
);

export default function SystemHealth() {
  const { token, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(() => {
    setBusy(true);
    axios.get(`${API}/admin/system-health`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setData(r.data); setErr(null); })
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load system health'))
      .finally(() => setBusy(false));
  }, [token]);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 30000);
    return () => window.clearInterval(t);
  }, [load]);

  if (!isAdmin) return <p className="p-6 text-slate-400 text-sm" data-testid="sh-forbidden">Administrators only.</p>;

  const qb = data?.quickbooks || {};
  const em = data?.email || {};
  const counts = data?.counts || {};
  const jobs = data?.background_jobs || [];
  const jobsHealthy = jobs.length > 0 && jobs.every((j) => j.healthy);
  const jobsStarting = !jobsHealthy && jobs.some((j) => j.last_run === null) && !jobs.some((j) => j.last_run !== null && !j.healthy);

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid="system-health-page">
      <div className="flex items-center gap-2">
        <Activity className="w-5 h-5 text-blue-300" />
        <h1 className="text-lg font-bold text-white">System Health</h1>
        <span className="text-[11px]" style={{ color: '#64748B' }}>
          {data?.checked_at ? `checked ${new Date(data.checked_at).toLocaleTimeString()}` : ''} · auto-refreshes every 30 s
        </span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={load} disabled={busy} data-testid="sh-refresh-btn">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>
      {err && <p className="text-red-400 text-sm" data-testid="sh-error">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="Database" icon={Database} ok={data?.database?.ok} testId="sh-database">
          <Row label="Connection" value={data?.database?.ok ? 'Healthy' : 'UNREACHABLE'} />
          <Row label="Active workers" value={counts.active_users ?? '—'} />
          <Row label="Open shifts right now" value={counts.open_shifts ?? '—'} />
          <Row label="Open exceptions" value={counts.open_exceptions ?? '—'} />
        </Panel>

        <Panel title="QuickBooks" icon={FileCheck} ok={qb.connected} testId="sh-quickbooks">
          <Row label="Connected & usable" value={qb.connected
            ? 'Yes'
            : qb.flag_set
              ? 'Marked connected but tokens missing — reconnect in Settings'
              : 'No — connect in Settings'} />
          <Row label="Company (realm) configured" value={qb.realm_configured ? 'Yes' : 'No'} />
          <Row label="Last payroll push" value={qb.last_push
            ? `${qb.last_push.start_date} → ${qb.last_push.end_date} (${qb.last_push.entries_pushed} entries, ${qb.last_push.errors} errors)`
            : 'Never'} />
          <Row label="Sync errors (7 days)" value={qb.sync_errors_7d ?? 0} />
        </Panel>

        <Panel title="Email & Notifications" icon={Mail} ok={em.configured} testId="sh-email">
          <Row label="Email (SES) configured" value={em.configured ? 'Yes' : 'No — in-app only'} />
          <Row label="Notifications sent (24 h)" value={em.notifications_24h ?? 0} />
          <Row label="Company timezone" value={counts.timezone || 'Not set'} />
          <Row label="Pay period configured" value={counts.pay_period_configured ? 'Yes' : 'No'} />
        </Panel>

        <Panel title="Background Jobs" icon={Clock} ok={jobsHealthy ? true : (jobsStarting ? undefined : false)} testId="sh-jobs">
          {jobsStarting && (
            <p className="text-[11px] mb-1 text-amber-300" data-testid="sh-jobs-starting">Starting up — jobs report in after their first run.</p>
          )}
          {jobs.map((j) => (
            <div key={j.job} className="flex items-center gap-2 text-xs py-1 border-b border-[#1F2D3F] last:border-0" data-testid={`sh-job-${j.job}`}>
              <Dot ok={j.healthy} />
              <span className="text-white flex-1">{JOB_LABELS[j.job] || j.job}</span>
              <span style={{ color: '#94A3B8' }}>{ago(j.seconds_ago)}{j.note ? ` · ${j.note}` : ''}</span>
            </div>
          ))}
        </Panel>
      </div>
      <p className="text-[11px]" style={{ color: '#64748B' }}>
        A job shows unhealthy until its first run after a restart — auto clock-out runs within minutes, the others within their first interval.
      </p>
    </div>
  );
}
